import { useState, useRef, useEffect } from 'react';

interface ShareMenuProps {
  /** Text to share (the post body, truncated) */
  text: string;
  /** URL to share — falls back to current page */
  url?: string;
  /** Visual style */
  variant?: 'themed' | 'light';
  /** Theme colors (for 'themed' variant) */
  colors?: { muted: string; surface: string; text: string; primary: string };
}

interface ShareTarget {
  name: string;
  icon: string;
  buildUrl: (text: string, url: string) => string;
}

const SHARE_TARGETS: ShareTarget[] = [
  {
    name: 'X',
    icon: '𝕏',
    buildUrl: (text, url) =>
      `https://x.com/intent/tweet?text=${enc(clip(text, 240))}&url=${enc(url)}`,
  },
  {
    name: 'Facebook',
    icon: 'f',
    buildUrl: (_text, url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
  },
  {
    name: 'LinkedIn',
    icon: 'in',
    buildUrl: (text, url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}&summary=${enc(clip(text, 200))}`,
  },
  {
    name: 'WhatsApp',
    icon: 'W',
    buildUrl: (text, url) =>
      `https://wa.me/?text=${enc(`${clip(text, 300)}\n${url}`)}`,
  },
  {
    name: 'Threads',
    icon: '@',
    buildUrl: (text, url) =>
      `https://www.threads.net/intent/post?text=${enc(`${clip(text, 400)}\n${url}`)}`,
  },
];

function enc(s: string) { return encodeURIComponent(s); }
function clip(s: string, max: number) {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '\u2026';
}

export default function ShareMenu({ text, url, variant = 'themed', colors }: ShareMenuProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNativeShare = async () => {
    setOpen(false);
    try {
      await navigator.share({ text: clip(text, 300), url: shareUrl });
    } catch { /* user cancelled */ }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link', shareUrl);
    }
  };

  const handleTarget = (target: ShareTarget) => {
    setOpen(false);
    window.open(target.buildUrl(text, shareUrl), '_blank', 'noopener,width=600,height=500');
  };

  // Styles
  const isThemed = variant === 'themed' && colors;
  const menuBg = isThemed ? colors.surface : '#fff';
  const menuBorder = isThemed ? `${colors.muted}30` : '#e5e5e5';
  const menuText = isThemed ? colors.text : '#333';
  const mutedColor = isThemed ? colors.muted : '#888';
  const accentColor = isThemed ? colors.primary : '#333';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Share externally"
        className="inline-flex items-center gap-1 text-xs transition-opacity hover:opacity-80"
        style={{ color: mutedColor }}
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0-12.814a2.25 2.25 0 1 0 3.935-2.186 2.25 2.25 0 0 0-3.935 2.186Zm0 12.814a2.25 2.25 0 1 0 3.933 2.185 2.25 2.25 0 0 0-3.933-2.185Z" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute bottom-full right-0 mb-2 z-20 w-44 rounded-xl p-1.5 shadow-lg"
          style={{ backgroundColor: menuBg, border: `1px solid ${menuBorder}` }}
        >
          <p
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{ color: mutedColor }}
          >
            Share to
          </p>

          {SHARE_TARGETS.map((t) => (
            <button
              key={t.name}
              type="button"
              onClick={() => handleTarget(t)}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors hover:opacity-80"
              style={{ color: menuText }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accentColor}10`)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <span
                className="flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-black"
                style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
              >
                {t.icon}
              </span>
              {t.name}
            </button>
          ))}

          <div className="my-1 border-t" style={{ borderColor: menuBorder }} />

          {/* Copy link */}
          <button
            type="button"
            onClick={handleCopy}
            className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors hover:opacity-80"
            style={{ color: menuText }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accentColor}10`)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <span
              className="flex h-6 w-6 items-center justify-center rounded-md text-[10px]"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              {copied ? '✓' : '🔗'}
            </span>
            {copied ? 'Copied!' : 'Copy link'}
          </button>

          {/* Native share (mobile) */}
          {hasNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors hover:opacity-80"
              style={{ color: menuText }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accentColor}10`)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <span
                className="flex h-6 w-6 items-center justify-center rounded-md text-[10px]"
                style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
              >
                ↗
              </span>
              More...
            </button>
          )}
        </div>
      )}
    </div>
  );
}
